import { useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { REFINE_STEPS } from "../constants";
import { useRefineStore } from "../store/RefineProvider";
import type { RefineStepId } from "../types";
import { validateRefineState } from "../validators";
import { RefineStepper } from "./RefineStepper";
import { ApplicationStep } from "./steps/ApplicationStep";
import { DescriptionStep } from "./steps/DescriptionStep";
import { DetailsStep } from "./steps/DetailsStep";
import { SkillsStep } from "./steps/SkillsStep";
import { TitleCategoryStep } from "./steps/TitleCategoryStep";

export function RefinePage() {
  const { t } = useI18n();
  const { state, goToStep, completeStep } = useRefineStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [validationErrors, setValidationErrors] = createSignal<
    Record<number, string[]>
  >({});

  const sectionRefs: Partial<Record<RefineStepId, HTMLElement>> = {};

  onMount(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const stepId = Number(
              entry.target.getAttribute("data-step"),
            ) as RefineStepId;
            goToStep(stepId);
            for (let i = 1; i < stepId; i++) {
              completeStep(i as RefineStepId);
            }
          }
        }
      },
      { rootMargin: "-30% 0px -70% 0px" },
    );

    for (const step of REFINE_STEPS) {
      const el = sectionRefs[step.id];
      if (el) observer.observe(el);
    }

    onCleanup(() => observer.disconnect());
  });

  const scrollToStep = (stepId: RefineStepId) => {
    sectionRefs[stepId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSaveAndPreview = (): void => {
    const result = validateRefineState(state);
    if (!result.success) {
      setValidationErrors(result.errorsByStep);
      const firstStepWithError = Math.min(
        ...Object.keys(result.errorsByStep).map(Number),
      ) as RefineStepId;
      scrollToStep(firstStepWithError);
      toast.error(t("jobOffers.refine.validationError") as string);
      return;
    }
    setValidationErrors({});
    const previewUrl = "/job-offers/create/refine/preview";
    if (!searchParams.next) {
      navigate(previewUrl);
      return;
    }
    navigate(`${previewUrl}${window.location.search}`);
  };

  return (
    <>
      <MetaTitleWithSeparator>
        {t("jobOffers.refine.pageTitle")}
      </MetaTitleWithSeparator>

      <div class="sticky top-0 z-10 border-b border-border bg-card">
        <div class="flex items-center gap-3 px-4 lg:px-8 py-3">
          <button
            type="button"
            class="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground shrink-0"
            onClick={() => navigate(-1)}
          >
            <i class="ph ph-arrow-left text-base" />
          </button>
          <div class="min-w-0">
            <h1 class="text-base font-semibold leading-tight truncate">
              {t("jobOffers.refine.pageTitle")}
            </h1>
            <p class="text-xs text-muted-foreground leading-tight truncate">
              {t("jobOffers.refine.pageDescription")}
            </p>
          </div>
        </div>

        <Container size="5xl" class="pb-3">
          <RefineStepper onStepClick={scrollToStep} />
        </Container>
      </div>

      <div class="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <section
          ref={(el) => {
            sectionRefs[1] = el;
          }}
          data-step="1"
          class="scroll-mt-28"
        >
          <TitleCategoryStep />
          <StepErrors errors={validationErrors()[1]} />
        </section>
        <section
          ref={(el) => {
            sectionRefs[2] = el;
          }}
          data-step="2"
          class="scroll-mt-28"
        >
          <DetailsStep />
          <StepErrors errors={validationErrors()[2]} />
        </section>
        <section
          ref={(el) => {
            sectionRefs[3] = el;
          }}
          data-step="3"
          class="scroll-mt-28"
        >
          <SkillsStep />
          <StepErrors errors={validationErrors()[3]} />
        </section>
        <section
          ref={(el) => {
            sectionRefs[4] = el;
          }}
          data-step="4"
          class="scroll-mt-28"
        >
          <DescriptionStep />
          <StepErrors errors={validationErrors()[4]} />
        </section>
        <section
          ref={(el) => {
            sectionRefs[5] = el;
          }}
          data-step="5"
          class="scroll-mt-28"
        >
          <ApplicationStep />
          <StepErrors errors={validationErrors()[5]} />
        </section>

        <div class="flex justify-end pt-4 border-t border-border">
          <Button size="lg" onClick={handleSaveAndPreview}>
            {t("jobOffers.refine.saveAndPreview") as string}
          </Button>
        </div>
      </div>
    </>
  );
}

function StepErrors(props: { errors?: string[] }) {
  return (
    <Show when={props.errors && props.errors.length > 0}>
      <div class="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
        <For each={props.errors}>{(error) => <p>{error}</p>}</For>
      </div>
    </Show>
  );
}
